import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useDebounce } from "react-use"

import type { WidgetPreviewSettings } from "@productlane/lib/src/widget-preview-message"
import { cn } from "@productlane/lib/src/browser"
import {
	WIDGET_PREVIEW_BACKGROUND,
	WIDGET_PREVIEW_SETTINGS_MESSAGE,
} from "@productlane/lib/src/widget-preview-message"
import { useDarkMode } from "@productlane/ui"

import { env } from "@/env"

interface Props {
	widgetKey: string | null | undefined
	settingsHash: string
	// Settings the author is editing right now, pushed into the preview so it
	// repaints on the change instead of waiting for the save and a refetch.
	liveSettings?: WidgetPreviewSettings
	view?: "home" | "ai"
	// Lets the previewed agent answer while the workspace still has it off.
	aiPreviewToken?: string
	// Shows the resting Spotlight bar in place of the launcher.
	spotlight?: boolean
	emptyMessage?: string
	// Fills its container instead of floating as a sticky card, for the
	// full-height settings rail.
	fill?: boolean
	className?: string
}

export function WidgetPreview({
	widgetKey,
	settingsHash,
	liveSettings,
	view = "home",
	aiPreviewToken,
	spotlight = false,
	emptyMessage = "Generate the widget snippet to see a live preview here.",
	fill = false,
	className,
}: Props) {
	const { isDarkMode } = useDarkMode()
	const [debouncedHash, setDebouncedHash] = useState(settingsHash)
	const iframeRef = useRef<HTMLIFrameElement | null>(null)

	useDebounce(() => setDebouncedHash(settingsHash), 500, [settingsHash])

	const src = useMemo(() => {
		if (!widgetKey) return null
		const base = env.VITE_API_URL.replace(/\/$/, "")
		const params = new URLSearchParams({
			key: widgetKey,
			mode: isDarkMode ? "dark" : "light",
			view,
		})
		if (aiPreviewToken) params.set("aiPreviewToken", aiPreviewToken)
		if (spotlight) params.set("spotlight", "1")
		return `${base}/widget-preview?${params.toString()}`
	}, [widgetKey, isDarkMode, view, aiPreviewToken, spotlight])

	const targetOrigin = useMemo(() => {
		try {
			return new URL(env.VITE_API_URL).origin
		} catch {
			return "*"
		}
	}, [])

	const liveSettingsJson = liveSettings ? JSON.stringify(liveSettings) : null
	const liveSettingsRef = useRef(liveSettingsJson)
	liveSettingsRef.current = liveSettingsJson

	const postLiveSettings = useCallback(() => {
		const json = liveSettingsRef.current
		const frame = iframeRef.current?.contentWindow
		if (!json || !frame) return
		frame.postMessage(
			{
				type: WIDGET_PREVIEW_SETTINGS_MESSAGE,
				previewSettings: JSON.parse(json) as WidgetPreviewSettings,
			},
			targetOrigin,
		)
	}, [targetOrigin])

	useEffect(() => {
		postLiveSettings()
	}, [liveSettingsJson, postLiveSettings])

	return (
		<div
			className={cn(
				fill
					? "flex h-full w-full min-w-0 flex-col"
					: "sticky top-8 hidden h-[calc(100dvh-8rem)] max-h-[720px] min-h-[480px] lg:block",
				className,
			)}
		>
			<div
				className={cn(
					"relative h-full w-full overflow-hidden",
					!fill && "rounded-xl border border-gray-200 dark:border-gray-800",
				)}
				style={{
					background: WIDGET_PREVIEW_BACKGROUND[isDarkMode ? "dark" : "light"],
				}}
			>
				{src ? (
					<iframe
						key={debouncedHash}
						ref={iframeRef}
						src={src}
						onLoad={postLiveSettings}
						title="Widget preview"
						className="h-full w-full border-0"
						sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
					/>
				) : (
					<div className="flex h-full items-center justify-center p-6 text-center text-sm text-secondary">
						{emptyMessage}
					</div>
				)}
			</div>
		</div>
	)
}
